<template>
    <!-- Empty state cuando no hay productos -->
    <div v-if="cartStore.items.length === 0" class="empty-state">
        <q-icon name="shopping_cart" size="80px" color="grey-4" />
        <div class="text-h6 text-grey-6 q-mt-md">Carrito vacío</div>
        <div class="text-body2 text-grey-5">Agrega productos para comenzar</div>
    </div>

    <!-- Lista de productos -->
    <div v-else>
        <q-card flat bordered class="summary-card q-mb-md">
            <q-card-section class="bg-primary text-white">
                <div class="row items-center">
                    <q-icon name="receipt_long" size="24px" class="q-mr-sm" />
                    <div class="text-subtitle1 text-weight-medium">
                        Resumen del Carrito
                    </div>
                </div>
            </q-card-section>
        </q-card>

        <q-list separator class="product-list">
            <q-item v-for="(item, index) in cartStore.items" :key="item.form.id" class="product-item">
                <q-item-section top avatar>
                    <q-avatar size="60px" rounded class="product-avatar q-ml-sm">
                        <q-icon :name="getProductIcon(item.producto.webGroupID)" size="32px"
                            :style="`color: ${useStringToColor(item.producto.webGroupDesc)};`"
                            />
                    </q-avatar>
                </q-item-section>

                <q-item-section>
                    <q-item-label class="text-weight-medium text-body1">
                        {{ item.producto.productoDesc }}
                    </q-item-label>
                    <q-item-label caption class="product-details">
                        <!-- Panel -->
                        <template v-if="item?.producto?.webGroupID.trim() === 'WPAN'">
                            <q-chip dense size="sm" icon="width" color="blue-1" text-color="blue-8">
                                Ancho: {{ numberFormat(item.form.ancho, true) }} m
                            </q-chip>
                            <q-chip dense size="sm" icon="height" color="blue-1" text-color="blue-8">
                                Largo: {{ numberFormat(item.form.largo_pieza, true) }} m
                            </q-chip>
                            <q-chip dense size="sm" icon="inventory_2" color="blue-1" text-color="blue-8">
                                {{ numberFormat(item.form.cantidad_piezas, true) }} piezas
                            </q-chip>
                            <q-chip dense size="sm" icon="calculate" color="primary" text-color="white">
                                {{ numberFormat(item.form.cantidad, true) }} m²
                            </q-chip>
                        </template>

                        <!-- Puerta -->
                        <template v-if="item?.producto?.webGroupID.trim() === 'WPUE'">
                            <q-chip v-if="item.form.accesorios?.length > 0" dense size="sm" icon="build_circle"
                                color="orange-1" text-color="orange-8">
                                {{ item.form.accesorios.length }} accesorios
                            </q-chip>
                            <q-chip dense size="sm" icon="format_list_numbered" color="green-1" text-color="green-8">
                                Cant: {{ numberFormat(item.form.cantidad, true) }}
                            </q-chip>
                        </template>

                        <!-- Equipo -->
                        <template v-if="item?.producto?.webGroupID.trim() === 'WEQU'">
                            <q-chip v-if="item.form.accesorios?.length > 0" dense size="sm" icon="build_circle"
                                color="orange-1" text-color="orange-8">
                                {{ item.form.accesorios.length }} accesorios
                            </q-chip>
                            <q-chip dense size="sm" icon="format_list_numbered" color="green-1" text-color="green-8">
                                Cant: {{ numberFormat(item.form.cantidad, true) }}
                            </q-chip>
                        </template>

                        <!-- Accesorio -->
                        <template v-if="item?.producto?.webGroupID.trim() === 'WACC'">
                            <q-chip dense size="sm" icon="format_list_numbered" color="green-1" text-color="green-8">
                                Cant: {{ numberFormat(item.form.cantidad, true) }}
                            </q-chip>
                        </template>
                    </q-item-label>
                </q-item-section>

                <q-item-section side>
                    <div class="column items-end q-gutter-xs">
                        <div class="text-h6 text-weight-bold text-primary">
                            {{ numberFormat(item.form.total) }}
                        </div>
                        <div class="row q-gutter-xs">
                            <q-btn round dense flat icon="edit" color="primary" size="sm"
                                @click="itemEdit(item, index)">
                                <q-tooltip>Editar producto</q-tooltip>
                            </q-btn>
                            <q-btn round dense flat icon="delete" color="negative" size="sm"
                                @click="confirmDelete(item)">
                                <q-tooltip>Eliminar producto</q-tooltip>
                            </q-btn>
                        </div>
                    </div>
                </q-item-section>
            </q-item>
        </q-list>

        <!-- Total Card -->
        <q-card flat bordered class="total-card q-mt-md">
            <q-card-section class="bg-grey-1">
                <div class="row items-center justify-between">
                    <div class="text-h6 text-weight-medium">
                        <q-icon name="calculate" class="q-mr-sm" />
                        Total
                    </div>
                    <div class="text-h5 text-weight-bold text-primary">
                        {{ total }}
                    </div>
                </div>
                <div class="row items-center justify-between">
                    <div class="text-h6 text-weight-medium">
                        <q-icon name="square_foot" class="q-mr-sm" />
                        Total M² (Paneles)
                    </div>
                    <div class="text-h5 text-weight-bold text-primary">
                        {{ totalM2 }} m²
                    </div>
                </div>
            </q-card-section>
        </q-card>
    </div>
</template>

<script setup>
import { computed } from "vue";
import { useCartStore } from "@/stores/useCartStore";
import { useQuasar } from "quasar";
import numberFormat from "@/helpers/numberFormat.ts";
import { useStringToColor } from "@/composables/useStringToColor";

const cartStore = useCartStore();
const $q = useQuasar();

const total = computed(() =>
    numberFormat(
        cartStore.items.reduce((x, y) => x + Number.parseFloat(y.form.total), 0)
    )
);

const totalM2 = computed(() =>
    numberFormat(
        cartStore.items
            .filter(item => item?.producto?.webGroupID.trim() === 'WPAN')
            .reduce((x, y) => x + Number.parseFloat(y.form.cantidad || 0), 0),
        true
    )
);

const getProductIcon = (webGroupID) => {
    const icons = {
        WPAN: 'view_quilt',
        WPUE: 'mdi-door',
        WEQU: 'ac_unit',
        WACC: 'build_circle',
        WCHA: 'mdi-saw-blade',
        WCNE: 'ac_unit',
    };
    return icons[webGroupID.trim()] || 'inventory';
};


const confirmDelete = (item) => {
    $q.dialog({
        title: "Eliminar Producto",
        message: `¿Está seguro de eliminar "${item.producto.productoDesc}" del carrito?`,
        cancel: {
            label: "Cancelar",
            color: "grey-7",
            flat: true,
        },
        ok: {
            label: "Eliminar",
            color: "negative",
            unelevated: true,
        },
        persistent: true,
    }).onOk(() => {
        itemDelete(item);
    });
};

const itemDelete = (item) => cartStore.itemDelete(item.form.id);
const itemEdit = (item, index) => cartStore.itemEdit(item, index);
</script>

<style lang="scss" scoped>
.empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
}

.summary-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.product-list {
    border-radius: 8px;
    overflow: hidden;
}

.product-item {
    transition: background-color 0.2s ease;
    padding: 16px 12px;

    &:hover {
        background-color: rgba(25, 118, 210, 0.04);
    }
}

.product-avatar {
    background: linear-gradient(135deg, rgba(25, 118, 210, 0.1) 0%, rgba(25, 118, 210, 0.05) 100%);
}

.product-details {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 8px;
}

.total-card {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    animation: fadeIn 0.4s ease-out;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}
</style>
